import {
  AccordionItemProps,
  AccordionRootProps,
  useAccordionItemContext,
} from '@chakra-ui/react';
import {
  IQuoteBillingScheduleRespSchema,
  ProductTypeEnum,
  RateBillingFrequencyEnum,
} from '@monetize/types/app';
import {
  formatCurrency,
  replaceUserTimezoneWithUtc,
  toDateShort,
} from '@monetize/utils/core';
import { subDays } from 'date-fns/subDays';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import React, { FC, useContext, useMemo } from 'react';
import {
  MAccordion,
  MAccordionCustomButton,
  MAccordionCustomButtonItem,
  MAccordionItem,
  MAccordionPanel,
  MText,
} from '../../../../components/Monetize';
import MRowWithLineSeparator from '../../../../components/Monetize/MRowWithLineSeparator';
import { RATE_BILLING_FREQUENCY_MAP } from '../../../../constants/offerings';
import { roundNumberToDecimal } from '../../../../utils/numberUtils';
import { getVariableAmountIndicator } from '../../../../utils/quotes';
import { QuoteContext } from '../quoteContext';

interface QuoteFormBillingScheduleProps extends AccordionRootProps {
  itemProps?: Omit<AccordionItemProps, 'value'>;
  loading?: boolean;
  anyVariableAmountProducts: boolean;
}

function getBillingFreq(
  billingFrequency: RateBillingFrequencyEnum,
  billingFrequencyInMonths?: number | null,
) {
  return RATE_BILLING_FREQUENCY_MAP[billingFrequency](billingFrequencyInMonths)
    .label;
}

interface QuoteFormBillingScheduleBodyProps {
  quoteBillingSchedule: IQuoteBillingScheduleRespSchema;
  billingScheduleListingV2: IQuoteBillingScheduleRespSchema['periods'];
  quoteCurrency?: string;
  variableAmountIndicator: string;
  hasDiscountProducts: boolean;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const QuoteFormBillingScheduleBody = ({
  quoteBillingSchedule,
  billingScheduleListingV2,
  quoteCurrency,
  variableAmountIndicator,
  hasDiscountProducts,
}: QuoteFormBillingScheduleBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton isExpanded={isExpanded} label="Billing Schedule">
        <MAccordionCustomButtonItem
          hideWhenExpanded
          isExpanded={isExpanded}
          label="Billing Frequency"
          minW={'130px'}
          value={getBillingFreq(
            quoteBillingSchedule.billingFrequency,
            quoteBillingSchedule.billingFrequencyInMonths,
          )}
        />
        <MAccordionCustomButtonItem
          hideWhenExpanded
          isExpanded={isExpanded}
          label="Billing Periods"
          minW={'111px'}
          value={billingScheduleListingV2.length}
        />
      </MAccordionCustomButton>

      <MAccordionPanel>
        {billingScheduleListingV2.map((period, i) => (
          <MRowWithLineSeparator
            key={i}
            leftLabel={
              <MText>
                {period.incrementalLabel ?? toDateShort(period.startDate)}
              </MText>
            }
            rightLabel={
              <MText>{`${formatCurrency(period.amount || 0, {
                currency: quoteCurrency,
              })}${variableAmountIndicator}`}</MText>
            }
          />
        ))}
        {!isNil(quoteBillingSchedule.incrementalPeriod) && (
          <>
            <MText fontSize="xs">
              Assumes Invoices have been generated up to{' '}
              {toDateShort(
                billingScheduleListingV2.length > 1
                  ? subDays(
                      replaceUserTimezoneWithUtc(
                        billingScheduleListingV2[1]?.startDate,
                      ),
                      1,
                    )
                  : quoteBillingSchedule?.incrementalPeriod?.billDate,
              )}
            </MText>
            <MText fontSize="xs">
              Amount = Incremental Invoice(s) - Credit Note(s) created upon
              execution of this Amendment quote.
            </MText>
          </>
        )}
        {hasDiscountProducts ? (
          <MText fontSize="xs" mt={4}>
            The billing schedule reflects standard charges only. Any applicable
            credits or discounts associated with Discount Products will be
            applied at the time of invoicing.
          </MText>
        ) : (
          <MText fontSize="xs" mt={4}>
            The billing schedule reflects standard charges only. Any applicable
            credits will be applied at the time of invoicing.
          </MText>
        )}
      </MAccordionPanel>
    </>
  );
};

const QuoteFormBillingSchedule: FC<QuoteFormBillingScheduleProps> =
  React.forwardRef<any, QuoteFormBillingScheduleProps>(
    (
      {
        itemProps,
        anyVariableAmountProducts,
        loading,
        ...rest
      }: QuoteFormBillingScheduleProps,
      ref,
    ) => {
      const { quoteData } = useContext(QuoteContext);

      const addIncrementalPeriodInBillingSchedule = (
        billingSchedule: IQuoteBillingScheduleRespSchema,
      ) => {
        if (isUndefined(billingSchedule)) {
          return [];
        }

        if (isNil(billingSchedule?.incrementalPeriod)) {
          return billingSchedule.periods;
        }

        const totalAmount =
          (billingSchedule?.incrementalPeriod?.billableAmount ?? 0) +
          (billingSchedule?.incrementalPeriod?.creditableAmount ?? 0);

        const newPeriod = {
          incrementalLabel: 'Upon Contract Execution',
          startDate: billingSchedule.incrementalPeriod?.billDate!,
          amount: roundNumberToDecimal(totalAmount),
        };

        const updatedPeriods = [newPeriod, ...billingSchedule.periods];

        return updatedPeriods;
      };

      const billingScheduleListingV2 = useMemo(
        () =>
          quoteData?.quoteBillingSchedule
            ? addIncrementalPeriodInBillingSchedule(
                quoteData.quoteBillingSchedule,
              )
            : [],
        [quoteData],
      );

      if (!quoteData?.quoteBillingSchedule) {
        return null;
      }

      const { quoteBillingSchedule, quote } = quoteData;

      const variableAmountIndicator = getVariableAmountIndicator(
        anyVariableAmountProducts,
      );

      const hasDiscountProducts = (quote?.quoteOfferings ?? []).some(
        (offering) =>
          (offering.items ?? []).some(
            (item) => item.productType === ProductTypeEnum.DISCOUNT,
          ),
      );

      return (
        <MAccordion collapsible defaultValue={['0']} {...rest}>
          <MAccordionItem
            borderColor="tGray.lightPurple"
            {...itemProps}
            value="0"
          >
            <QuoteFormBillingScheduleBody
              quoteBillingSchedule={quoteBillingSchedule}
              billingScheduleListingV2={billingScheduleListingV2}
              quoteCurrency={quote?.currency}
              variableAmountIndicator={variableAmountIndicator}
              hasDiscountProducts={hasDiscountProducts}
            />
          </MAccordionItem>
        </MAccordion>
      );
    },
  );

export default QuoteFormBillingSchedule;
